import { z } from "zod";
import {
  FactNodeSchema,
  type FactEdgeMetadata,
  type FactNodeContent,
} from "./FactNode.js";
import { OrganizationFactNodeContentSchema } from "./FactNodes/OrganizationFactNode.js";
import { RootConnectsWithFactEdgeMetadataSchema } from "./FactNodes/RootFactNode.js";

export const companyMetadata = {
  nodeType: OrganizationFactNodeContentSchema.shape.nodeType.value,
  nodeVersion: OrganizationFactNodeContentSchema.shape.version.value,
  connectsWithEdgeType:
    RootConnectsWithFactEdgeMetadataSchema.shape.edgeType.value,
  connectsWithEdgeVersion:
    RootConnectsWithFactEdgeMetadataSchema.shape.version.value,
  connectsWithEdgeRelationship: "company",
};

export const CompanySchema = z.object({
  id: FactNodeSchema.shape.id,
  creatorId: FactNodeSchema.shape.creatorId,
  createdAt: FactNodeSchema.shape.createdAt,
  removedAt: FactNodeSchema.shape.removedAt,
  createdByAssistant: FactNodeSchema.shape.createdByAssistant,
  isPrivate: FactNodeSchema.shape.private,
  name: OrganizationFactNodeContentSchema.shape.name,
});

export const CompanyRawSchema = z.object({
  id: FactNodeSchema.shape.id,
  creator_id: FactNodeSchema.shape.creatorId,
  created_at: FactNodeSchema.shape.createdAt,
  removed_at: FactNodeSchema.shape.removedAt,
  private: FactNodeSchema.shape.private,
  node_type: FactNodeSchema.shape.nodeType,
  content: OrganizationFactNodeContentSchema,
  organization_id: FactNodeSchema.shape.organizationId,
  created_by_assistant: FactNodeSchema.shape.createdByAssistant,
});

export const CreateCompanySchema = z.object({
  name: CompanySchema.shape.name,
  isPrivate: CompanySchema.shape.isPrivate.optional(),
});

export const UpdateCompanySchema = z.object({
  name: CompanySchema.shape.name,
  isPrivate: CompanySchema.shape.isPrivate.optional(),
});

export const FilterCompanyParamsSchema = z.object({
  skip: z.int().optional(),
  take: z.int().max(500).optional(),
});

export type Company = z.infer<typeof CompanySchema>;
export type CompanyRaw = z.infer<typeof CompanyRawSchema>;
export type CompanyContent = Extract<
  FactNodeContent,
  { nodeType: "organization" }
>;
export type ConnectsWithCompanyMetadata = Extract<
  FactEdgeMetadata,
  { edgeType: "connectsWith" }
>;
export type CreateCompany = z.infer<typeof CreateCompanySchema>;
export type UpdateCompany = z.infer<typeof UpdateCompanySchema>;
export type FilterCompanyParams = z.infer<typeof FilterCompanyParamsSchema>;

export const ListCompaniesToolInputSchema = z.object({
  query: z.string().optional().describe("Filter companies by name."),
  limit: z.number().int().min(1).max(100).default(50),
  offset: z.number().int().min(0).optional(),
});
export const CompanyToolOutputSchema = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.string(),
});
export type CompanyToolOutput = z.infer<typeof CompanyToolOutputSchema>;

export function toCompanyToolOutput(company: {
  id: string;
  name: string;
  createdAt: Date;
}): CompanyToolOutput {
  return {
    id: company.id,
    name: company.name,
    createdAt: company.createdAt.toISOString(),
  };
}
